<template>
  <template v-if="currentAdmin">
    <header>
      <nav class="navbar navbar-light navbar-expand-lg bg-white shadow-sm border-bottom fixed-top">
        <div class="container-fluid">
          <router-link class="navbar-brand" style="line-height: 16px;" :to="{ name: 'admin.home' }">dmp</router-link>
          <button class="navbar-toggler" type="button" @click="isNavOpen = !isNavOpen">
            <span class="navbar-toggler-icon"></span>
          </button>
          <div class="collapse navbar-collapse" :class="{ show: isNavOpen }" id="navbarSupportedContent">
            <ul class="navbar-nav">
              <li class="nav-item">
                <RouterLink :to="{ name: 'admin.admins.index' }" class="nav-link" :class="{ active: route.name.startsWith('admin.admins') }">{{ $t('common.admin') }}</RouterLink>
              </li>
              <li class="nav-item">
                <RouterLink :to="{ name: 'admin.users.index' }" class="nav-link" :class="{ active: route.name.startsWith('admin.users') }">{{ $t('common.users') }}</RouterLink>
              </li>
              <li class="nav-item">
                <RouterLink :to="{ name: 'admin.user-registration-requests.index' }" class="nav-link" :class="{ active: route.name.startsWith('admin.user-registration-requests') }">{{ $t('common.userRegistrationRequests') }}</RouterLink>
              </li>
              <li class="nav-item">
                <RouterLink :to="{ name: 'admin.data-providers.index' }" class="nav-link" :class="{ active: route.name.startsWith('admin.data-providers') }">{{ $t('common.dataProviders') }}</RouterLink>
              </li>
              <li class="nav-item">
                <RouterLink :to="{ name: 'admin.collected-events.index' }" class="nav-link" :class="{ active: route.name.startsWith('admin.collected-events') }">{{ $t('common.collectedEvents') }}</RouterLink>
              </li>
              <li class="nav-item">
                <RouterLink :to="{ name: 'admin.master-data.index' }" class="nav-link" :class="{ active: route.name.startsWith('admin.master-data') }">{{ $t('common.masterData') }}</RouterLink>
              </li>
              <li class="nav-item">
                <RouterLink :to="{ name: 'admin.postal-codes.index' }" class="nav-link" :class="{ active: route.name.startsWith('admin.postal-codes') }">{{ $t('common.postalCodes') }}</RouterLink>
              </li>
              <li class="nav-item">
                <RouterLink :to="{ name: 'admin.api-call-histories.index' }" class="nav-link" :class="{ active: route.name.startsWith('admin.api-call-histories') }">{{ $t('common.systemLogs') }}</RouterLink>
              </li>
            </ul>
            <ul class="navbar-nav ms-auto">
              <li class="nav-item dropdown">
                <a class="nav-link dropdown-toggle" href="#" ref="dropdownToggle" @click.prevent="toggleDropdown">{{ currentAdmin?.name }}</a>
                <ul class="dropdown-menu dropdown-menu-end">
                  <!-- Language selection submenu -->
                  <li class="nav-item dropdown">
                  <a class="dropdown-item dropdown-toggle" href="#" @mouseenter="showLanguageMenu = true" @mouseleave="showLanguageMenu = false">
                    <i class="bi bi-translate me-2"></i>{{ $t('common.language') }}
                  </a>
                  <ul class="dropdown-menu dropdown-submenu" :class="{ show: showLanguageMenu }" @mouseenter="showLanguageMenu = true" @mouseleave="showLanguageMenu = false">
                    <li><a class="dropdown-item" href="#" @click="setLanguage('ja')">🇯🇵 日本語</a></li>
                    <li><a class="dropdown-item" href="#" @click="setLanguage('en')">🇺🇸 English</a></li>
                    <li><a class="dropdown-item" href="#" @click="setLanguage('vi')">🇻🇳 Tiếng Việt</a></li>
                    <li><a class="dropdown-item" href="#" @click="setLanguage('th')">🇹🇭 ไทย</a></li>
                  </ul>
                  </li>
                  <!-- Divider -->
                  <li><hr class="dropdown-divider"></li>
                  <!-- Change password and logout options -->
                  <li><a class="dropdown-item" href="#" @click="changePassword">
                    <i class="bi bi-key me-2"></i>{{ $t('password.change') }}
                  </a></li>
                  <!-- Logout option -->
                  <li><a class="dropdown-item" href="#" @click="logoutAdmin">
                    <i class="bi bi-box-arrow-right me-2"></i>{{ $t('common.logout') }}
                  </a></li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </header>

    <main>
      <RouterView :key="route.path"/>
    </main>

    <footer>
    </footer>
  </template>
</template>

<script setup>
import {onMounted, ref, nextTick, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {useAuthAdmin} from '../../composables/useAuthAdmin.js';
import {setLocale} from '../../plugins/i18n.js';
import { Dropdown } from 'bootstrap';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const {logout, fetchCurrentAdmin, currentAdmin} = useAuthAdmin();
const showLanguageMenu = ref(false);
const dropdownToggle = ref(null);
let dropdownInstance = null;

// Mobile hamburger state, controlled by Vue instead of the Bootstrap data
// API: the bundle and the ESM import both register data-api handlers, which
// made the toggle unreliable, and an SPA navigation never closed the menu.
const isNavOpen = ref(false);

watch(() => route.fullPath, () => {
  isNavOpen.value = false;
});

onMounted(async () => {
  await fetchCurrentAdmin();
  
  // Initialize Bootstrap dropdown after Vue component is mounted
  nextTick(() => {
    if (dropdownToggle.value) {
      dropdownInstance = new Dropdown(dropdownToggle.value);
    }
  });
});

/**
 * Manually toggle dropdown
 */
const toggleDropdown = () => {
  if (dropdownInstance) {
    dropdownInstance.toggle();
  }
};

/**
 * Sign out
 */
const logoutAdmin = () => {
  logout();
};

/**
 * Navigate to change password page
 */
const changePassword = () => {
  router.push({ name: 'admin.change-password' });
};

/**
 * Set application language
 */
const setLanguage = (locale) => {
  setLocale(locale);
};

</script>


<style lang="scss" scoped>
button {
  color: #3e62ad;
  background-color: transparent;
  border-color: #ededed;
  border-radius: 50px;
}

header {
  color: black;
}

main {
  padding-top: 54px;
  height: 100%;
  background: #fafafa;
}

footer {
  background-color: #474a4d;
  color: #bccddb;
}

#content {
  padding-left: 250px;
  background: #fafafa;
  min-height: 100vh;
}

.dropdown-submenu {
  position: relative;
}

.dropdown-submenu .dropdown-menu {
  position: absolute !important;
  top: 0 !important;
  left: 100% !important;
  margin-top: -1px !important;
  transform: none !important;
  will-change: auto !important;
}

.dropdown-submenu:hover .dropdown-menu {
  display: block !important;
}

/* 右端に近い場合は左側に表示 */
.dropdown-menu-end .dropdown-submenu .dropdown-menu {
  left: -100% !important;
  right: 0 !important;
}
</style>
